import get from "lodash/get"

/**
 * Transforms the new Places API response structure to the legacy format
 * expected by the existing UI components.
 *
 * @see Migration mapping:
 * | Feature          | Legacy Property                        | New Property (Places API New)                    |
 * |------------------|----------------------------------------|--------------------------------------------------|
 * | Full Description | suggestion.description                 | suggestion.placePrediction.text.text             |
 * | Main Text        | structured_formatting.main_text        | suggestion.placePrediction.mainText.text         |
 * | Secondary Text   | structured_formatting.secondary_text   | suggestion.placePrediction.secondaryText.text    |
 * | Place ID         | place_id                               | suggestion.placePrediction.placeId               |
 *
 * @param {Object} suggestion - The new AutocompleteSuggestion object from Places API (New)
 * @returns {Object} - A suggestion object compatible with the legacy react-geosuggest format
 */
export function transformSuggestionToLegacy(suggestion) {
  const placePrediction = get(suggestion, "placePrediction", {})

  return {
    // Legacy format properties
    description: get(placePrediction, "text.text", ""),
    label: get(placePrediction, "text.text", ""),
    placeId: get(placePrediction, "placeId", ""),
    place_id: get(placePrediction, "placeId", ""), // alias for backwards compatibility

    // Structured formatting (legacy format)
    structured_formatting: {
      main_text: get(placePrediction, "mainText.text", ""),
      secondary_text: get(placePrediction, "secondaryText.text", ""),
      main_text_matched_substrings: get(
        placePrediction,
        "mainText.matches",
        [],
      ).map((match) => ({
        offset: match.startOffset || 0,
        length: (match.endOffset || 0) - (match.startOffset || 0),
      })),
    },

    // Keep the original new API object for advanced usage
    _original: suggestion,
    _placePrediction: placePrediction,
  }
}

/**
 * Transforms an array of new API suggestions to legacy format
 *
 * @param {Array} suggestions - Array of AutocompleteSuggestion objects
 * @returns {Array} - Array of legacy-format suggestion objects
 */
export function transformSuggestionsToLegacy(suggestions) {
  if (!Array.isArray(suggestions)) {
    return []
  }
  return suggestions.map(transformSuggestionToLegacy)
}

/**
 * Transforms place details from the new Places API to the legacy gmaps format.
 *
 * This function converts the Place object returned by place.fetchFields()
 * to a format compatible with the existing getFormattedGoogleAddress utility.
 *
 * @param {Object} place - The Place object from the new API
 * @param {Object} suggestion - The original suggestion object
 * @returns {Object} - An object compatible with the legacy gmaps/suggest format
 */
export function transformPlaceToLegacyGmaps(place, suggestion) {
  const placePrediction =
    get(suggestion, "_placePrediction", {}) ||
    get(suggestion, "placePrediction", {})

  // Extract location coordinates
  const location = place.location
  const lat = location ? location.lat() : null
  const lng = location ? location.lng() : null

  // Build address components from the new API format
  // The new API returns addressComponents as an array
  const addressComponents = (place.addressComponents || []).map((comp) => ({
    long_name: comp.longText || "",
    short_name: comp.shortText || "",
    types: comp.types || [],
  }))

  return {
    label: get(placePrediction, "text.text", "") || place.displayName || "",
    placeId: place.id || get(placePrediction, "placeId", ""),
    location: lat !== null && lng !== null ? { lat, lng } : null,
    gmaps: {
      formatted_address: place.formattedAddress || "",
      address_components: addressComponents,
      place_id: place.id,
      geometry: {
        location:
          lat !== null && lng !== null
            ? {
                lat: () => lat,
                lng: () => lng,
              }
            : null,
      },
    },
  }
}

export default {
  transformSuggestionToLegacy,
  transformSuggestionsToLegacy,
  transformPlaceToLegacyGmaps,
}
